import {
  File as FileIcon,
  FileArchive,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { api, unwrap } from "./api";
import type { AttachmentMeta } from "./board";

/** Client-side mirror of the server cap — reject early with a friendly message. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

export function isImageMime(mime: string): boolean {
  return mime.startsWith("image/");
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** A lucide icon component for a mime type (used by file chips). */
export function fileTypeIcon(mime: string) {
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime === "application/pdf") return FileText;
  if (mime === "application/zip") return FileArchive;
  if (mime.startsWith("text/") || mime === "application/json") return FileText;
  return FileIcon;
}

/** Upload files to a task. Returns the server metadata (each with a serve URL). */
export async function uploadAttachments(
  taskId: string,
  files: File[],
): Promise<AttachmentMeta[]> {
  const { attachments } = await unwrap(
    api.api.tasks({ id: taskId }).attachments.post({ files }),
  );
  return attachments;
}

/** Delete an own, not-yet-linked attachment (tray "remove" before submit). */
export async function deleteAttachment(id: string): Promise<void> {
  await unwrap(api.api.attachments({ id }).delete());
}

export type PendingAttachment = {
  localId: string;
  file: File;
  /** Object URL for an image preview while uploading; revoked on remove/reset. */
  previewUrl: string | null;
  status: "uploading" | "done" | "error";
  uploaded: AttachmentMeta | null;
  error: string | null;
};

/**
 * Manage a composer's pending attachment list: kick off a per-file upload on
 * add (so a slow file never blocks the others), track each file's status, and
 * expose the uploaded ids to submit + whether anything is still in flight (to
 * gate the send button). Object URLs are revoked on remove/reset to avoid leaks.
 */
export function useAttachmentUploads(taskId: string | null) {
  const [items, setItems] = useState<PendingAttachment[]>([]);

  // Revoke any outstanding object URLs when the composer unmounts.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  useEffect(
    () => () => {
      for (const it of itemsRef.current)
        if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
    },
    [],
  );

  const add = useCallback(
    (files: FileList | File[]) => {
      if (!taskId) return;
      for (const file of Array.from(files)) {
        const localId = crypto.randomUUID();
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setItems((p) => [
            ...p,
            {
              localId,
              file,
              previewUrl: null,
              status: "error",
              uploaded: null,
              error: "Too large (max 25 MB)",
            },
          ]);
          continue;
        }
        const previewUrl = file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : null;
        setItems((p) => [
          ...p,
          {
            localId,
            file,
            previewUrl,
            status: "uploading",
            uploaded: null,
            error: null,
          },
        ]);
        uploadAttachments(taskId, [file])
          .then(([meta]) =>
            setItems((p) =>
              p.map((it) =>
                it.localId === localId
                  ? { ...it, status: "done", uploaded: meta ?? null }
                  : it,
              ),
            ),
          )
          .catch((e: unknown) =>
            setItems((p) =>
              p.map((it) =>
                it.localId === localId
                  ? {
                      ...it,
                      status: "error",
                      error: e instanceof Error ? e.message : "Upload failed",
                    }
                  : it,
              ),
            ),
          );
      }
    },
    [taskId],
  );

  const remove = useCallback((localId: string) => {
    setItems((prev) => {
      const it = prev.find((x) => x.localId === localId);
      if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      // best-effort server cleanup of an uploaded-but-unlinked file
      if (it?.uploaded) void deleteAttachment(it.uploaded.id).catch(() => {});
      return prev.filter((x) => x.localId !== localId);
    });
  }, []);

  const reset = useCallback(() => {
    setItems((prev) => {
      for (const it of prev) if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      return [];
    });
  }, []);

  const doneIds = items
    .filter((i) => i.status === "done" && i.uploaded)
    .map((i) => i.uploaded!.id);
  const uploading = items.some((i) => i.status === "uploading");

  return { items, add, remove, reset, doneIds, uploading };
}
